import clsx from 'clsx';
import { useFormik } from 'formik';
import React from 'react';
import { getSignInSchema } from './schema';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { Link, useNavigate } from 'react-router';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import { toast } from 'react-toastify';
import { AppDispatch } from '../../store/store';
import { useDispatch } from 'react-redux';
import { USER_LOGIN_SUCCESS } from '../../reducers/constants';
import { useApiClients } from '../../client/api/useApiClient';
import { useLocalStorage } from '@uidotdev/usehooks';
import { LoginResponseData } from '@EHSNavigator/backend';

interface SignInProps {
  otherClasses?: string;
}

const INITIAL_STATE = {
  email: '',
  password: '',
};

const SignIn: React.FC<SignInProps> = ({ otherClasses }) => {
  const signInClasses = clsx(
    otherClasses,
    'w-full max-w-325 lg:max-w-465 mx-auto flex flex-col items-center gap-5 md:gap-8 lg:gap-10',
  );
  const dispatch = useDispatch<AppDispatch>();
  const [, setUserInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );
  const navigate = useNavigate();
  const { AuthApi } = useApiClients();
  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    handleBlur,
    touched,
    isSubmitting,
    resetForm,
  } = useFormik({
    initialValues: INITIAL_STATE,
    validationSchema: getSignInSchema(),
    onSubmit: async (values) => {
      try {
        const response = await AuthApi.authControllerLogin({
          loginUserDTO: {
            email: String(values?.email),
            password: String(values?.password),
          },
        });
        const user = response?.data?.data;
        toast.success(response?.data?.message);
        dispatch({
          type: USER_LOGIN_SUCCESS,
          payload: user,
        });
        setUserInfo(user);
        resetForm();
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        if (errorMessage?.includes('not subscribed')) {
          navigate(`/pricing-plans?email=${encodeURIComponent(values.email)}`);
        }
        if (Array.isArray(errorMessage)) {
          errorMessage.forEach((msg: string) => toast.error(msg));
        } else {
          toast.error(errorMessage || 'An unexpected error occurred.');
        }
      }
    },
  });
  const sizes = useButtonSizeLarge();

  return (
    <form
      action="#"
      onSubmit={handleSubmit}
      className={signInClasses}
      data-testid="sign-in"
      aria-labelledby="sign-in-heading"
      noValidate
    >
      <h1 id="sign-in-heading" className="sr-only">
        Sign In
      </h1>
      <Input
        onBlur={handleBlur}
        label="Email*"
        errorMessage={touched.email && errors.email ? errors.email : ''}
        disabled={isSubmitting}
        value={values.email}
        type="email"
        placeholder="someone@gmail.com"
        onChange={handleChange}
        name="email"
        otherClasses={{ container: 'w-full' }}
        aria-required={true}
        aria-invalid={touched.email && !!errors.email}
        aria-describedby={
          touched.email && errors.email ? 'email-error' : undefined
        }
      />
      <Input
        onBlur={handleBlur}
        label="Password*"
        errorMessage={
          touched.password && errors.password ? errors.password : ''
        }
        disabled={isSubmitting}
        value={values.password}
        type="password"
        placeholder="Type password"
        onChange={handleChange}
        name="password"
        otherClasses={{ container: 'w-full' }}
        aria-required={true}
        aria-invalid={touched.password && !!errors.password}
        aria-describedby={
          touched.password && errors.password ? 'password-error' : undefined
        }
      />
      <Button
        size={sizes}
        type="submit"
        aria-label="Sign In"
        disable={isSubmitting}
        otherClasses="w-full max-w-465 sm:mt-0 mt-2"
      >
        Sign In
      </Button>
      <nav
        className="mt-7 flex flex-col items-center gap-5 text-center lg:mt-0"
        aria-label="Sign in options"
      >
        <Link
          to="/forgot-password"
          className="font-inter-variable text-brand-byzantine text-base leading-5 font-medium md:text-lg lg:text-xl lg:leading-6"
          aria-label="Forgot Password"
        >
          Forgot Password?
        </Link>
        <p className="font-inter-variable text-neutral-tones-text text-base leading-5 font-medium md:text-lg lg:text-xl lg:leading-6">
          Don't have an account?{' '}
          <Link
            to="/sign-up"
            className="text-brand-byzantine block lg:inline"
            aria-label="Create a New Account"
          >
            Create a New Account
          </Link>
        </p>
      </nav>
    </form>
  );
};

export default SignIn;
